import { useSession } from "@/services/auth0/session";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

// Unified Order Creation Types
export interface Address {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  name?: string; // Optional recipient/sender name for rate requests
}

export interface PackageInput {
  weight: number;
  weightUnit: "g" | "kg" | "lb" | "oz";
  length: number;
  width: number;
  height: number;
  dimensionUnit: "cm" | "m" | "in" | "ft";
  packageKind?: "PARCEL" | "THICK_ENVELOPE";
  description?: string;
  isFragile?: boolean;
  requiresCooling?: boolean;
  declaredValueCents?: number;
}

/**
 * Pricing-redesign: echo of the chosen `PricingOption` from `get-quote`.
 * Server recomputes via the composer for drift detection — diverging > 5%
 * returns 409 PRICING_DRIFT with new prices.
 */
export interface SelectedRate {
  /**
   * ⚠️ THE ORDER CARRIES A QUOTE ID, NOT A PRICE (#533).
   *
   * `labelPriceCents` and `purchaseHandle` used to be here. The server billed
   * whatever price this body contained, and the token that BUYS the label made a
   * round trip through the browser. Send the id; the server bills the row it wrote.
   */
  quoteId: string;
  carrierId: string;
  carrierServiceCode: string;
  fromServiceAreaId: string;
  toServiceAreaId: string;
  currency: string;
}

/** Optional pickup add-on attached to a shipment in the new pricing model. */
export interface PickupAddOn {
  pickupServiceId: string;
  scheduledDate: string; // ISO date
  slotId: string;
  notes?: string;
  /**
   * ⚠️ No `pickupPriceCents`. Credit redemption sized a customer's spending CAP
   * from it, so an inflated pickup price let a customer raise their own ceiling.
   * The tier is the customer's choice; its price is not.
   */
}

export interface ShipmentInput {
  // ── Pricing-redesign canonical shape ─────────────────────────────────────
  /** Required for new orders. Echoed from `PricingOption` returned by get-quote. */
  selectedRate?: SelectedRate;
  /** Optional pickup add-on (booking is find-or-created server-side). */
  pickup?: PickupAddOn;
  /**
   * Re-submit flag: set true after the server returned 409 PRICING_DRIFT and
   * the user has confirmed the new prices. Bypasses the 5% drift threshold
   * for that shipment only.
   */
  confirmRePricing?: boolean;

  // Sender details
  senderName: string;
  senderEmail?: string;
  senderPhone?: string;
  /**
   * Phase 10a.5: when set, server reuses the entry's Address row so
   * PickupBooking dedup matches across orders. Mutually exclusive with
   * shipFrom — provide exactly one.
   */
  shipFromAddressBookEntryId?: string;
  shipFrom?: Address;

  // Recipient details
  recipientName: string;
  recipientEmail?: string;
  recipientPhone?: string;
  shipToAddressBookEntryId?: string;
  shipTo?: Address;

  // Packages
  packages: PackageInput[];

  // Delivery notes (printed on labels for our drivers, ignored by external carriers)
  deliveryNotes?: string;
}

export interface UnifiedOrderRequest {
  placerCustomerId?: string;
  metadata?: Record<string, unknown>;
  shipments: ShipmentInput[];
  paymentMethodType?: "SAVED_CARD" | "PAYMENT_LINK" | "CREDIT" | "INVOICE";
  paymentTermsId?: string; // For NET terms orders
  // Note: Credit is applied when paymentMethodType === "CREDIT"
  paymentMetadata?: {
    netDays?: number;
    type?: string;
    reference?: string;
  };
}

export interface CreatedPackage {
  packageId: string;
  trackingNumber: string;
}

export interface CreatedTask {
  taskId: string;
  type: string;
}

export interface CreatedShipment {
  shipmentId: string;
  packages: CreatedPackage[];
  tasks: CreatedTask[];
}

export interface UnifiedOrderResponse {
  orderId: string;
  orderTrackingNumber: string;
  shipments: CreatedShipment[];
}

// Bulk Order Types
export interface BulkOrderRequest {
  orders: UnifiedOrderRequest[];
  stopOnError?: boolean;
}

export interface BulkValidationRequest {
  orders: UnifiedOrderRequest[];
}

export interface BulkOrderResult {
  success: boolean;
  orderId?: string;
  orderTrackingNumber?: string;
  error?: string;
  validationErrors?: {
    path: string[];
    message: string;
    code: string;
  }[];
}

export interface BulkOrderResponse {
  summary: {
    total: number;
    successful: number;
    failed: number;
  };
  results: BulkOrderResult[];
}

export interface BulkValidationResponse {
  summary: {
    total: number;
    valid: number;
    invalid: number;
  };
  results: {
    valid: boolean;
    errors?: {
      path: string[];
      message: string;
      code: string;
    }[];
  }[];
}

// ========================= Unified Order Creation =========================

export function useCreateUnifiedOrder(options?: {
  onSuccess?: (data: UnifiedOrderResponse) => void;
  /**
   * Error callback. The original AxiosError is preserved on `error.cause` so
   * the caller can detect 409 PRICING_DRIFT via the type guard
   * `isPricingDriftError` from `@/api/get-quote`.
   */
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();

  return useMutation({
    mutationFn: async (
      order: UnifiedOrderRequest
    ): Promise<UnifiedOrderResponse> => {
      const { data } = await axios.post<UnifiedOrderResponse>(
        `${API_URL}/api/v2/orders`,
        order,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data;
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}

// ========================= Bulk Order Operations =========================

export function useBulkCreateOrders(options?: {
  onSuccess?: (data: BulkOrderResponse) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();

  return useMutation({
    mutationFn: async (
      request: BulkOrderRequest
    ): Promise<BulkOrderResponse> => {
      const { data } = await axios.post<BulkOrderResponse>(
        `${API_URL}/api/v2/orders/bulk-create`,
        request,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data;
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}

export function useBulkValidateOrders(options?: {
  onSuccess?: (data: BulkValidationResponse) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();

  return useMutation({
    mutationFn: async (
      request: BulkValidationRequest
    ): Promise<BulkValidationResponse> => {
      const { data } = await axios.post<BulkValidationResponse>(
        `${API_URL}/api/v2/orders/bulk-validate`,
        request,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data;
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
}
